<template>
  <div>
    <q-toolbar color="white" text-color="grey-9" class="top-page-toolbar">
      <q-toolbar-title class="q-body-2">
        <router-link class="text-grey-10 no-underline" :to="{ name: 'expenses-dashboard', params: { teamId: activeTeam.id } }">
          {{ $tc('expense', 2) }}
        </router-link> / <span class="text-weight-bold">{{ $t('overview') }}</span>
      </q-toolbar-title>

      <q-btn
        color="primary"
        class="q-mt-sm"
        @click="$router.push({ name: 'capture-expenses' })"
      >
        {{ $t('capture_x_expenses_btn', { count: notCapturedExpenses.value }) }}
      </q-btn>
    </q-toolbar>
    <div class="layout-padding">
      <div class="row gutter-md">
        <div class="col-12 col-xl-5">
          <expense-upload
            class="full-height"
            :uploadFunction="expenseUploadFunction"
            @uploaded="handleExpensesUploaded"
          />
        </div>

        <div class="col-12 col-xl-7">
           <q-card class="bg-white full-height">
            <q-card-main class="q-py-xl">
              <bar
                v-if="expensesStatistics"
                :chartdata="expensesChartdata"
                :options="expensesChartOptions"
                style="height: 250px"
              />
            </q-card-main>
          </q-card>
        </div>
      </div>

      <div class="row justify-between items-end q-mb-md q-mt-lg">
        <span class="q-title text-bold text-blue-grey-4">{{ $t('uncaptured_expenses_heading') }}</span>

        <router-link
          :to="{ name: 'capture-expenses' }"
          class="router-link q-subheading text-blue-grey-4"
        >
          {{ $t('capture_x_expenses_btn', { count: '' }) }}
        </router-link>
      </div>

      <div class="row gutter-md">
        <div v-for="expense in uncapturedExpenses" :key="expense.id" class="col-auto">
          <q-card-media
            style="max-height: 250px;"
            @click.native="$router.push({ name: 'expense', params: { teamId: activeTeam.id, expenseId: expense.id } })"
          >
            <expense-preview
              :model="expense"
              width="200"
              class="cursor-pointer"
            />
          </q-card-media>
        </div>

        <div v-if="uncapturedExpenses && uncapturedExpenses.length === 0" class="column justify-center text-center full-width q-ma-lg q-mt-xl">
          <img src="~assets/emptystates/expenses_empty.svg" height="100px" />
          <p class="q-mt-md text-faded">{{ $t('all_expenes_captured') }}</p>
        </div>
      </div>

      <div class="row justify-between items-end q-mb-md q-mt-lg">
        <span class="q-title text-bold text-blue-grey-4">{{ $t('recent_expenses_heading') }}</span>

        <router-link
          :to="{ name: 'expenses-list', params: { teamId: activeTeam.id } }"
          class="router-link q-subheading text-blue-grey-4"
        >
          {{ $t('view_all') }}
        </router-link>
      </div>

      <div class="row">
        <expense-list
          :expenses="expenses"
          @deleted-expense="handleExpenseDeleted"
        />

        <div v-if="expenses && expenses.length === 0" class="column justify-center text-center full-width q-ma-lg q-mt-xl">
          <img src="~assets/emptystates/expenses_empty.svg" height="100px" />
          <p class="q-mt-md text-faded">{{ $t('no_recent_expenses_available') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import Bar from 'components/Charts/RoundedBar.vue'
  import ExpenseList from 'components/Expenses/ExpenseList.vue'
  import ExpensePreview from 'components/Common/DocumentPreview.vue'
  import ExpenseUpload from 'components/Common/DocumentUpload.vue'
  import { ExpensesClient } from 'src/api/Expenses'
  import { date } from 'quasar'
  import { formatMoney } from 'src/utils/format'
  import parseDate from 'date-fns/parse'
  import store from 'src/store'

  export default {
    components: {
      ExpenseList,
      ExpenseUpload,
      Bar,
      ExpensePreview
    },
    data () {
      return {
        expenses: null,
        uncapturedExpenses: null,
        meta: null
      }
    },
    computed: {
      ...mapGetters({ activeTeam: 'activeTeam', expensesStatistics: 'expensesStatistic', notCapturedExpenses: 'notCapturedExpenses' }),
      expensesChartdata () {
        return {
          labels: this.expensesStatistics.value.map((entry) => date.formatDate(parseDate(entry.start_date), 'MMMM YYYY')),
          datasets: [{
            label: this.$tc('expense', 2),
            data: this.expensesStatistics.value.map((entry) => entry.value),
            backgroundColor: '#4c51bf',
            hoverBackgroundColor: '#434190',
            maxBarThickness: 25,
            minBarLength: 3
          }]
        }
      },
      expensesChartOptions () {
        return {
          layout: {
            padding: {
              left: 10,
              top: 10,
              right: 10,
              bottom: 10
            }
          },
          legend: {
            display: false
          },
          tooltips: {
            displayColors: false,
            callbacks: {
              label: (tooltipItem, data) => {
                return formatMoney(tooltipItem.yLabel)
              }
            }
          },
          scales: {
            yAxes: [{
              display: 'auto',
              gridLines: {
                borderDash: [2],
                borderDashOffset: [2],
                zeroLineBorderDash: [2],
                zeroLineBorderDashOffset: [2],
                color: '#cbd5e0',
                zeroLineColor: '#cbd5e0',
                drawBorder: false,
                drawTicks: false
              },
              ticks: {
                callback: (value) => formatMoney(value),
                suggestedMin: 0,
                fontFamily: "'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                fontColor: '#718096',
                padding: 15
              }
            }],
            xAxes: [{
              display: 'auto',
              gridLines: {
                borderDash: [2],
                borderDashOffset: [2],
                zeroLineBorderDash: [2],
                zeroLineBorderDashOffset: [2],
                color: '#cbd5e0',
                zeroLineColor: '#cbd5e0',
                drawBorder: false,
                drawOnChartArea: false,
                drawTicks: false
              },
              ticks: {
                fontFamily: "'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                fontColor: '#718096',
                padding: 15
              }
            }]
          }
        }
      }
    },
    methods: {
      ...mapActions(['fetchStatisticsOverview']),
      handleRequest ({ pagination, filter }) {
        new ExpensesClient()
          .page(pagination.page)
          .limit(pagination.rowsPerPage)
          .sort('created_at:desc')
          .list()
          .then(response => {
            const expenses = response.data.data
            const meta = response.data.meta

            this.expenses = expenses
            this.meta = meta
          })
      },
      expenseUploadFunction (file) {
        return new ExpensesClient()
          .create({})
          .then(response => response.data.data)
          .then(expense => {
            const formData = new FormData()
            formData.append('document', file.file)

            return new ExpensesClient()
              .attachDocument(expense.id, formData)
          })
      },
      handleExpensesUploaded () {
        store.dispatch('fetchStatisticsOverview')

        new ExpensesClient()
          .filter([['complete', '!=', 'true']])
          .include('supplier')
          .limit(8)
          .sort('created_at:desc')
          .list()
          .then(response => {
            this.uncapturedExpenses = response.data.data
          })
      },
      handleExpenseDeleted (expenseId) {
        const index = this.expenses.findIndex(expense => expense.id === expenseId)

        if (index !== -1) {
          this.expenses.splice(index, 1)
        }
      }
    },
    beforeRouteEnter (to, from, next) {
      const expenseRequest = new ExpensesClient()
        .filter([['complete', '=', 'true']])
        .include('supplier')
        .limit(5)
        .sort('created_at:desc')
        .list()
        .then(response => ({
          expenses: response.data.data,
          meta: response.data.meta
        }))

      const uncapturedExpensesRequest = new ExpensesClient()
        .filter([['complete', '!=', 'true']])
        .include('supplier')
        .limit(8)
        .sort('created_at:desc')
        .list()
        .then(response => ({
          expenses: response.data.data,
          meta: response.data.meta
        }))

      const statistics = store.dispatch('fetchStatisticsOverview')

      Promise.all([expenseRequest, uncapturedExpensesRequest, statistics])
        .then(([{ expenses, meta }, { expenses: uncapturedExpenses }]) => {
          next(vm => {
            vm.expenses = expenses
            vm.uncapturedExpenses = uncapturedExpenses
            vm.meta = meta
          })
        })
    },
    beforeRouteUpdate (to, from, next) {
      const expenseRequest = new ExpensesClient()
        .filter([['complete', '=', 'true']])
        .include('supplier')
        .limit(5)
        .list()
        .then(response => ({
          expenses: response.data.data,
          meta: response.data.meta
        }))

      const uncapturedExpensesRequest = new ExpensesClient()
        .filter([['complete', '!=', 'true']])
        .include('supplier')
        .limit(8)
        .sort('created_at:desc')
        .list()
        .then(response => ({
          expenses: response.data.data,
          meta: response.data.meta
        }))

      const statistics = store.dispatch('fetchStatisticsOverview')

      Promise.all([expenseRequest, uncapturedExpensesRequest, statistics])
        .then(([{ expenses, meta }, { expenses: uncapturedExpenses }]) => {
          this.expenses = expenses
          this.uncapturedExpenses = uncapturedExpenses
          this.meta = meta
          next()
        })
    }
  }
</script>
